import { H1 } from '@berlitz/globals'
import PortalStudentProfile from '@classes/StudentProfile'
import AtRiskNotificationCard from '@components/AtRiskNotificationCard'
import MenuTab, { MenuTabItem } from '@components/MenuTab'
import HeaderWithClock from '@components/PageHeader'
import Scroller from '@components/Scroller'
import { StoreProps } from '@interfaces/StoreState'
import PendingLessonsTab from '@layouts/Scheduling/SchedulingLayout/PendingLessonsTab'
import PendingLessonsTabQuery from '@layouts/Scheduling/SchedulingLayout/PendingLessonsTabQuery'
import includes from 'lodash/includes'
import map from 'lodash/map'
import Link from 'next/link'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { Col, Row } from 'react-styled-flexboxgrid'
import menu, { pendingLessonContracts, TAB_IDS, TAB_NUMBERS } from './menu'
import { ContentBox, Inline, MenuTabBox, StyledGrid } from './style'
import { PROFILE_TYPE } from '@utils/constants'
import Error from '@components/Error'

interface SchedulingProp {
  children: React.ReactNode
  activeTab: number
  withSidebar?: boolean
  showClock?: boolean
  dontFetchPending?: boolean
  role?: string
}

const SchedulingLayout: React.FC<SchedulingProp> = ({
  children,
  activeTab = 1,
  withSidebar,
  showClock,
  dontFetchPending,
}) => {
  const { canViewPendingPage, role, profile } = useSelector(({ userProfile }: StoreProps) => ({
    canViewPendingPage: includes(
      pendingLessonContracts,
      (userProfile?.info?.InstructorProfile?.instructorInfo?.ContractType ?? '').toLowerCase()
    ),
    role: userProfile?.info?.Type?.toLowerCase() || 'student',
    profile: new PortalStudentProfile(userProfile.info.StudentProfile?.StudentProfileInfo),
  }))

  // Check if Book tab should be hidden for students with only F2F programs or 2B students
  const shouldHideBookTab = (menuIdOrTabNumber: string | number) => {
    const isStudent = role === PROFILE_TYPE.STUDENT.toLowerCase();
    const isStudentBookTab = 
      menuIdOrTabNumber === TAB_IDS.STUDENT_BOOK || 
      menuIdOrTabNumber === TAB_NUMBERS.STUDENT_BOOK;
    return isStudent && isStudentBookTab && (profile?.is2bStudentOnly || profile?.onlyF2FPrograms);
  };
  
  if (shouldHideBookTab(activeTab)) {
    return <Error statusCode={404} />
  }

  return (
    <StyledGrid fluid withSidebar={withSidebar}>
      {showClock ? (
        <HeaderWithClock title="Scheduling" />
      ) : (
        <H1>
          <FormattedMessage id="Scheduling" />
        </H1>
      )}

      <Scroller>
        <MenuTabBox>
          <MenuTab activeTab={activeTab}>
            {map(menu[role], item => {
              // Hide "Book" tab for students who only have F2F programs or are 2B students only
              if (shouldHideBookTab(item.id)) {
                return null
              }

              if (role === PROFILE_TYPE.INSTRUCTOR.toLowerCase() && item.id === TAB_IDS.INSTRUCTOR_PENDING_LESSONS && !canViewPendingPage) {
                return null
              }

              return role === PROFILE_TYPE.INSTRUCTOR.toLowerCase() && item.id === TAB_IDS.INSTRUCTOR_PENDING_LESSONS && canViewPendingPage ? (
                <MenuTabItem key={item.tab}>
                  {dontFetchPending ? null : <PendingLessonsTabQuery />}
                  <PendingLessonsTab item={item} />
                </MenuTabItem>
              ) : (
                <MenuTabItem key={item.tab}>
                  <Link href={item.link} legacyBehavior>
                    <Inline>{item.child} </Inline>
                  </Link>
                </MenuTabItem>
              )
            })}
          </MenuTab>
        </MenuTabBox>
      </Scroller>
      <ContentBox hasNotif={!!profile.AtRiskProgram}>
        <Row>
          <Col xs={12} md={12} lg={activeTab !== TAB_NUMBERS.STUDENT_BOOK ? 10 : 7}>
            <AtRiskNotificationCard noButton={activeTab === TAB_NUMBERS.STUDENT_BOOK} />
          </Col>
        </Row>
        {children}
      </ContentBox>
    </StyledGrid>
  )
}

export default SchedulingLayout